import { useEffect, useRef } from 'react';

import { useAnalytics } from '@/hooks/analytics';

import { MentalHealthSectionTitle, MentalHealthSlide } from '../MentalHealthTools/types';
import { AudioElement, Wrapper } from './styles';

const AudioPlayer = ({
	slide,
	currentSection,
	analyticsPrefix = 'Homepage',
}: {
	slide: MentalHealthSlide;
	currentSection: MentalHealthSectionTitle;
	analyticsPrefix?: string;
}) => {
	const audioPlayer = useRef<HTMLAudioElement>(null);
	const { logEvent } = useAnalytics();

	useEffect(() => {
		const currentAudioPlayer = audioPlayer.current;
		const onPlay = () => {
			logEvent({
				eventName: `${analyticsPrefix} : Audio Content : Played`,
				eventProps: {
					slide_name: slide.title.defaultMessage as string,
				},
			});
		};

		currentAudioPlayer?.addEventListener('play', onPlay);

		return () => {
			currentAudioPlayer?.removeEventListener('play', onPlay);
		};
	}, [analyticsPrefix, logEvent, slide.title]);

	useEffect(() => {
		const currentAudioPlayer = audioPlayer.current;
		const onPause = () => {
			const currentTime = audioPlayer?.current?.currentTime;
			logEvent({
				eventName: `${analyticsPrefix} : Audio Content : Paused`,
				eventProps: {
					slide_name: slide.title.defaultMessage as string,
					...(currentTime ? { current_time: currentTime } : {}),
				},
			});
		};

		currentAudioPlayer?.addEventListener('pause', onPause);

		return () => {
			currentAudioPlayer?.removeEventListener('pause', onPause);
		};
	}, [analyticsPrefix, logEvent, slide.title]);

	useEffect(() => {
		audioPlayer.current?.pause();
	}, [currentSection]);

	// Stops the content after 5 min if user is in preview mode
	useEffect(() => {
		const currentAudioPlayer = audioPlayer?.current;
		if (currentAudioPlayer && slide.isPreview && !audioPlayer?.current?.paused) {
			const currentTime = currentAudioPlayer?.currentTime;
			if (currentTime > 300) {
				currentAudioPlayer.pause();
				// TODO: refactor this code to follow best practices
				// eslint-disable-next-line react-hooks/immutability
				currentAudioPlayer.currentTime = 0;
			}
		}
	}, [slide, audioPlayer?.current?.paused, audioPlayer?.current?.currentTime, currentSection]);

	return (
		<Wrapper>
			<AudioElement
				controls
				preload="none"
				controlsList="nodownload"
				src={slide.audioSrc}
				ref={audioPlayer}
			/>
		</Wrapper>
	);
};

export default AudioPlayer;
